import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { animeArticles, siteInfo, categories } from '@/mocks/anime';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(
    () => animeArticles.find((a) => a.id === id),
    [id]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return animeArticles
      .filter((a) => a.id !== article.id && a.category === article.category)
      .slice(0, 4);
  }, [article]);

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center text-foreground-300">
                <i className="ri-file-unknow-line text-4xl"></i>
              </div>
              <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
              <p className="text-foreground-500 text-sm mb-4">抱歉，您要查看的内容不存在或已被移除</p>
              <Link
                to="/"
                className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
          <ScrollToTop />
        </div>
      </>
    );
  }

  const articleUrl = `${siteInfo.url}/article/${article.id}`;
  const categoryInfo = categories.find((c) => c.slug === article.category);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: siteInfo.url,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${article.categoryName}动漫`,
        item: `${siteInfo.url}/category/${article.category}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: articleUrl,
      },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    dateModified: article.updateDate,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    image: article.coverImage,
  };

  const jsonLd = [breadcrumbSchema, articleSchema];

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.description}
        keywords={article.tags.join(',')}
        canonicalUrl={articleUrl}
        ogType="article"
        ogImage={article.coverImage}
        articlePublishedTime={article.publishDate}
        articleModifiedTime={article.updateDate}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-4">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <Link
                to={`/category/${article.category}`}
                className="hover:text-primary-500 transition-colors cursor-pointer"
              >
                {article.categoryName}动漫
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-700">{article.title}</span>
            </nav>
          </div>

          {/* Article Header */}
          <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.year}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.episodes}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-400">{article.status}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 flex-wrap">
                <span>作者：{article.author}</span>
                <span>评分：<strong className="text-accent-500 font-semibold">{article.rating}</strong></span>
                <span>浏览：{article.views.toLocaleString()}</span>
                <span>发布于 {article.publishDate}</span>
              </div>
            </header>

            {/* Cover Image */}
            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                title={`${article.title} - 樱花动漫`}
                className="w-full max-h-[400px] object-cover object-top"
              />
            </div>

            {/* Content */}
            <div
              className="prose prose-sm md:prose-base max-w-none mb-10 text-foreground-800"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="flex items-center gap-2 flex-wrap mb-10 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-xs font-medium bg-background-100 text-foreground-600 rounded-full border border-background-200/70 whitespace-nowrap"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full max-w-4xl mx-auto px-4 md:px-6 pb-16">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-2">
                      <div className="aspect-[3/4] w-full">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute top-1.5 left-1.5">
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-primary-500 text-background-50 rounded-full whitespace-nowrap">
                          {rel.rating}分
                        </span>
                      </div>
                    </div>
                    <h3 className="text-xs font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {rel.title}
                    </h3>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}